import type { Event as SentryEvent } from "@sentry/react";

const SENSITIVE_KEYS = [
  "password",
  "token",
  "authorization",
  "email",
  "phone",
  "telegram",
  "cookie",
  "refreshToken",
  "accessToken",
  "auth",
  // Extended PII keys
  "full_name",
  "fullName",
  "display_name",
  "displayName",
  "first_name",
  "firstName",
  "last_name",
  "lastName",
  "bio",
  "description",
  "content",
  "message",
  "address",
  "passport",
  "snils",
  "inn",
];
const SAFE_KEYS = [
  "trace_id",
  "request_id",
  "session_id",
  "user_id",
  "userId",
  "anonymous_id",
  "listingId",
  "petId",
  "curatorId",
  "entityId",
  "helpRequestId",
  "roomId",
  "chatRoomId",
  "breedId",
  "speciesKey",
];

const emailRegex = /([\w.+-]+)@([\w.-]+)/i;
// Russian phone patterns: +7, 8, or international format
const phoneRegex = /(?:\+7|8|7)[\s(-]*\d{3}[\s)-]*\d{3}[\s-]*\d{2}[\s-]*\d{2}|\+?\d[\d\s().-]{6,}\d/;
// Card number pattern
const cardRegex = /\d{4}[\s-]?\d{4}[\s-]?\d{4}[\s-]?\d{4}/;
// Russian SNILS pattern (XXX-XXX-XXX XX)
const snilsRegex = /\d{3}[\s-]?\d{3}[\s-]?\d{3}[\s-]?\d{2}/;
// Russian INN pattern (10 or 12 digits)
const innRegex = /\b\d{10,12}\b/;

function createMask(value: string, visibleStart = 2, visibleEnd = 2): string {
  if (value.length <= visibleStart + visibleEnd) {
    return "***";
  }
  const start = value.slice(0, visibleStart);
  const end = value.slice(-visibleEnd);
  return `${start}***${end}`;
}

function maskEmail(value: string): string {
  const match = value.match(emailRegex);
  if (!match) return createMask(value);
  const [, localPart, domain] = match;
  return `${createMask(localPart, 1, 1)}@${domain}`;
}

function maskPhone(value: string): string {
  return value.replace(/\d(?=\d{2})/g, "*");
}

function looksSensitive(value: string): boolean {
  return (
    emailRegex.test(value) ||
    phoneRegex.test(value) ||
    cardRegex.test(value) ||
    snilsRegex.test(value) ||
    value.length > 24
  );
}

function maskCard(value: string): string {
  // Mask all but last 4 digits
  return value.replace(/\d(?=\d{4})/g, "*");
}

function sanitizeString(value: string, key?: string): string {
  if (key && SAFE_KEYS.includes(key)) {
    return value;
  }
  if (key && SENSITIVE_KEYS.some((sensitiveKey) => key.toLowerCase().includes(sensitiveKey.toLowerCase()))) {
    return createMask(value);
  }
  if (emailRegex.test(value)) return maskEmail(value);
  if (phoneRegex.test(value)) return maskPhone(value);
  if (cardRegex.test(value)) return maskCard(value);
  if (snilsRegex.test(value)) return createMask(value);
  // Truncate long text fields in logs (PII protection + log size)
  if (value.length > 200) {
    return `${value.slice(0, 50)}...[truncated ${value.length} chars]...${value.slice(-20)}`;
  }
  if (looksSensitive(value)) return createMask(value);
  return value;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return Object.prototype.toString.call(value) === "[object Object]";
}

function sanitizeValue(value: unknown, key?: string, seen?: WeakSet<object>): unknown {
  if (value === null || value === undefined) return value;
  if (typeof value === "string") return sanitizeString(value, key);
  if (typeof value !== "object") return value;

  const safeSeen = seen ?? new WeakSet<object>();
  if (safeSeen.has(value as object)) return "[Circular]";
  safeSeen.add(value as object);

  if (Array.isArray(value)) {
    return value.map((item) => sanitizeValue(item, key, safeSeen));
  }

  if (value instanceof Date) return value.toISOString();
  if (!isPlainObject(value)) return value;

  const sanitizedEntries: Record<string, unknown> = {};
  Object.entries(value).forEach(([entryKey, entryValue]) => {
    sanitizedEntries[entryKey] = sanitizeValue(entryValue, entryKey, safeSeen);
  });
  return sanitizedEntries;
}

export function sanitizePayload<T>(payload: T): T {
  try {
    return sanitizeValue(payload) as T;
  } catch {
    // Silent fallback - can't use logger here as sanitize is used by logger
    return payload;
  }
}

type SanitizedEvent = SentryEvent & { user?: { id?: string; username?: string; ip_address?: string } };

export function sanitizeSentryEvent(event: SentryEvent): SentryEvent {
  const sanitized = sanitizePayload(event) as SanitizedEvent;
  if (sanitized.user) {
    sanitized.user = sanitizePayload({
      id: sanitized.user.id,
      username: sanitized.user.username,
      ip_address: undefined,
    });
  }
  return sanitized;
}
